import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { useSite } from '../lib/site-context';

export interface PageHeroAction {
  label: string;
  to: string;
}

export interface PageHeroProps {
  eyebrow: string;
  title: string;
  description: string;
  content?: string;
  action?: PageHeroAction;
}

interface SplitTitleResult {
  start: string;
  middle: string;
  end: string;
  error: string | null;
}

export function PageHero({
  eyebrow,
  title,
  description,
  action,
}: PageHeroProps) {
  const { site } = useSite();

  // Helper de configuração seguro
  const getSetting = (key: string, fallback: string): string => {
    return site?.settings?.[key] ?? fallback;
  };

  // Divide o título em 3 segmentos com tratamento de exceção
  const { start, middle, end, error } = useMemo<SplitTitleResult>(() => {
    try {
      if (typeof title !== 'string') {
        throw new TypeError(`Esperava-se uma string para "title", recebido: ${typeof title}`);
      }

      const length = title.length;
      if (length === 0) {
        return { start: '', middle: '', end: '', error: null };
      }

      const segmentSize = Math.floor(length / 3);
      const startText = title.slice(0, segmentSize);
      const middleText = title.slice(segmentSize, segmentSize * 2);
      const endText = title.slice(segmentSize * 2);

      return {
        start: startText,
        middle: middleText,
        end: endText,
        error: null,
      };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao processar título.';
      return {
        start: title || '',
        middle: '',
        end: '',
        error: errorMessage,
      };
    }
  }, [title]);

  return (
    <section className="page-hero">
      <div className="container page-hero-content">
        {/* Notificação de erro sem interromper o fluxo visual */}
        {error && (
          <div 
            role="alert" 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              color: '#d97706', 
              fontSize: '0.875rem',
              marginBottom: '8px'
            }}
          >
            <AlertTriangle size={16} aria-hidden="true" />
            <span>Aviso de renderização: {error}</span>
          </div>
        )}

        <span className="eyebrow">
          <span className="status-dot" aria-hidden="true" />
          {eyebrow}
        </span>

        <h1 className="hero-title">
          {start}
          {middle && <span className="gradient-text">{middle}</span>}
          {end}
        </h1>

        <p className="hero-description">{description}</p>

        {action && (
          <Link className="btn btn-primary" to={action.to}>
            {action.label}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        )}
      </div>
    </section>
  );
}